import React, { useState, useEffect, useRef } from "react";

const FindPost = ({
	register,
	error,
	required = false,
	className = "",
	id,
	field,
	setValue,
	watch,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showResults, setShowResults] = useState(false);
	const [selectedPost, setSelectedPost] = useState(null);
	const [imageUrls, setImageUrls] = useState({}); // Cache for featured image URLs (keeping for future use)
	const searchTimeoutRef = useRef(null);
	const resultsRef = useRef(null);

	// Get the current value to show selected post
	const currentValue = watch(id);

	// Function to get URL parameter value
	const getUrlParameter = (name) => {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get(name);
	};

	// Function to fetch post by ID
	const fetchPostById = async (postId) => {
		try {
			const postType = field.post_type || "post";
			const response = await fetch(
				`${window.location.origin}/wp-json/wp/v2/${postType}/${postId}?_embed&meta=1`
			);
			if (response.ok) {
				const post = await response.json();
				return post;
			} else {
				console.error("Failed to fetch post:", response.statusText);
				return null;
			}
		} catch (error) {
			console.error("Error fetching post:", error);
			return null;
		}
	};

	// Check for pre-fill parameter on component mount
	useEffect(() => {
		if (field.slug) {
			const prefilledId = getUrlParameter(field.slug);
			if (prefilledId && !selectedPost && !currentValue) {
				const loadPrefilledPost = async () => {
					setIsLoading(true);
					const post = await fetchPostById(prefilledId);
					if (post) {
						setSelectedPost(post);
						setSearchTerm(post.title.rendered);
						setValue(id, post.id);

						// Register tribute_name field when a post is pre-filled
						if (post.acf && post.acf.tribute_name) {
							setValue("tribute_name", post.acf.tribute_name);
						}
					}
					setIsLoading(false);
				};
				loadPrefilledPost();
			}
		}
	}, [field.slug, selectedPost, currentValue, setValue, id]);

	// Fetch posts from WordPress REST API
	const searchPosts = async (term) => {
		if (!term || term.length < 2) {
			setSearchResults([]);
			return;
		}

		setIsLoading(true);
		try {
			const postType = field.post_type || "post";
			const response = await fetch(
				`${
					window.location.origin
				}/wp-json/wp/v2/${postType}?search=${encodeURIComponent(
					term
				)}&per_page=10&_embed&meta=1`
			);
			if (response.ok) {
				const posts = await response.json();
				setSearchResults(posts);
			} else {
				console.error("Failed to fetch posts:", response.statusText);
				setSearchResults([]);
			}
		} catch (error) {
			console.error("Error searching posts:", error);
			setSearchResults([]);
		} finally {
			setIsLoading(false);
		}
	};

	// Get featured image URL from the response data
	const getFeaturedImage = (post) => {
		// Check if we have embedded media data and it's not an error
		if (
			post._embedded &&
			post._embedded["wp:featuredmedia"] &&
			post._embedded["wp:featuredmedia"][0] &&
			!post._embedded["wp:featuredmedia"][0].code &&
			post._embedded["wp:featuredmedia"][0].source_url
		) {
			return post._embedded["wp:featuredmedia"][0].source_url;
		}

		// Try to get from Yoast head JSON (often contains featured image)
		if (
			post.yoast_head_json &&
			post.yoast_head_json.og_image &&
			post.yoast_head_json.og_image[0]
		) {
			return post.yoast_head_json.og_image[0].url;
		}

		// If we have a featured_media ID but no URL, construct a likely path
		// This is a fallback - you might need to adjust the path based on your WordPress setup
		if (post.featured_media && post.featured_media > 0) {
			// You could try constructing the URL if you know your media URL pattern
			// For now, return null and show placeholder
			return null;
		}

		return null;
	};

	// Debounced search
	useEffect(() => {
		// Don't search if a post is already selected
		if (selectedPost) {
			return;
		}

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		searchTimeoutRef.current = setTimeout(() => {
			searchPosts(searchTerm);
		}, 300);

		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, [searchTerm, selectedPost]);

	// Handle post selection
	const handlePostSelect = (post) => {
		setSelectedPost(post);
		setSearchTerm(post.title.rendered);
		setValue(id, post.id);

		// Register tribute_name field when a post is selected
		if (post.acf && post.acf.tribute_name) {
			setValue("tribute_name", post.acf.tribute_name);
		}

		setShowResults(false);
	};

	// Handle input change
	const handleInputChange = (e) => {
		// Disallow search if a post is already selected
		if (selectedPost) {
			return;
		}

		const value = e.target.value;
		setSearchTerm(value);
		setShowResults(true);
	};

	// Handle input focus
	const handleInputFocus = () => {
		// Only show results if no post is selected
		if (searchResults.length > 0 && !selectedPost) {
			setShowResults(true);
		}
	};

	// Handle click outside to close results
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (resultsRef.current && !resultsRef.current.contains(event.target)) {
				setShowResults(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Load selected post data if we have a value
	useEffect(() => {
		if (currentValue && !selectedPost) {
			const fetchSelectedPost = async () => {
				try {
					const postType = field.post_type || "post";
					const response = await fetch(
						`${window.location.origin}/wp-json/wp/v2/${postType}/${currentValue}?_embed&meta=1`
					);
					if (response.ok) {
						const post = await response.json();
						setSelectedPost(post);
						setSearchTerm(post.title.rendered);

						// Register tribute_name field when loading a selected post
						if (post.acf && post.acf.tribute_name) {
							setValue("tribute_name", post.acf.tribute_name);
						}
					}
				} catch (error) {
					console.error("Error fetching selected post:", error);
				}
			};
			fetchSelectedPost();
		}
	}, [currentValue, selectedPost, field.post_type]);

	return (
		<div className={`find-post-field ${className}`} ref={resultsRef}>
			<input
				{...register(id, {
					required: required ? "Please select a post" : false,
				})}
				type="hidden"
			/>

			{!selectedPost && (
				<div className="find-post-input-wrapper">
					<input
						type="text"
						value={searchTerm}
						onChange={handleInputChange}
						onFocus={handleInputFocus}
						placeholder={field.placeholder || "Search posts..."}
						className={`find-post-input ${error ? "field__input--error" : ""}`}
					/>

					{isLoading && (
						<div className="find-post-loading">
							<div className="spinner"></div>
						</div>
					)}
				</div>
			)}

			{!selectedPost && showResults && searchResults.length > 0 && (
				<div className="find-post-results">
					{searchResults.map((post) => (
						<div
							key={post.id}
							className="find-post-result-item"
							onClick={() => handlePostSelect(post)}
						>
							<div className="find-post-result-image find-post-result-image--large">
								{getFeaturedImage(post) ? (
									<img
										src={getFeaturedImage(post)}
										alt={post.title.rendered}
										onError={(e) => {
											e.target.style.display = "none";
										}}
									/>
								) : (
									<div className="find-post-result-placeholder">
										<svg viewBox="0 0 24 24" fill="currentColor">
											<path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
										</svg>
									</div>
								)}
							</div>
							<div className="find-post-result-title">
								{post.title.rendered}
							</div>
						</div>
					))}
				</div>
			)}

			{selectedPost && (
				<div className="find-post-selected">
					<div className="find-post-selected-image">
						{getFeaturedImage(selectedPost) ? (
							<img
								src={getFeaturedImage(selectedPost)}
								alt={selectedPost.title.rendered}
								onError={(e) => {
									e.target.style.display = "none";
								}}
							/>
						) : (
							<div className="find-post-selected-placeholder">
								<svg viewBox="0 0 24 24" fill="currentColor">
									<path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
								</svg>
							</div>
						)}
					</div>
					<div className="find-post-selected-content">
						<h4 className="find-post-selected-title">
							{selectedPost.title.rendered}
						</h4>

						<h6 className="find-post-county">
							{selectedPost.acf.tribute_county}
						</h6>
					</div>
				</div>
			)}

			{error && <div className="field__error">{error.message}</div>}
		</div>
	);
};

export default FindPost;
